/*
 * Back9 quiz — transport adapter.
 *
 * Everything the quiz UI knows about the backend lives here. The UI calls
 * named operations and reads one normalized envelope; it never sees fetch,
 * status codes, or the mock. Swapping `mode` from 'mock' to 'proxy' is the
 * only change required when the Shopify app proxy is connected.
 *
 * Contract: back9-experiments/docs/QUIZ_BACKEND_CONTRACT.md
 */
(() => {
  'use strict';

  if (window.Back9QuizApi) return;

  const DEFAULT_TIMEOUT_MS = 8000;

  // The contract classifies every failure into exactly one of these. Keeping
  // the mapping in one place is what lets the outbox stay dumb.
  const OUTCOME = Object.freeze({
    OK: 'OK',
    RETRYABLE: 'RETRYABLE',
    CONFLICT: 'CONFLICT',
    CHALLENGE: 'CHALLENGE',
    TERMINAL: 'TERMINAL'
  });

  const RETRYABLE_STATUSES = new Set([408, 425, 429]);

  const classify = (status) => {
    if (status >= 200 && status < 300) return OUTCOME.OK;
    if (status === 403) return OUTCOME.CHALLENGE;
    if (status === 409) return OUTCOME.CONFLICT;
    if (RETRYABLE_STATUSES.has(status)) return OUTCOME.RETRYABLE;
    if (status >= 500) return OUTCOME.RETRYABLE;
    return OUTCOME.TERMINAL;
  };

  const toBase64Url = (bytes) => {
    let binary = '';
    for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  // 16 cryptographically random browser bytes as unpadded Base64url, which is
  // the 22-character shape both `requestId` and `eventId` require. Without a
  // real RNG we return null and the caller disables tracking rather than
  // inventing a weaker identifier.
  const randomId = () => {
    const crypto = window.crypto || window.msCrypto;
    if (!crypto || typeof crypto.getRandomValues !== 'function') return null;
    return toBase64Url(crypto.getRandomValues(new Uint8Array(16)));
  };

  // Canonical ISO UTC with millisecond precision.
  const timestamp = (date) => new Date(date || Date.now()).toISOString();

  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  };

  const post = async (url, payload, timeoutMs) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = window.setTimeout(() => controller?.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller?.signal
      });

      const parsed = await parseJson(response);
      return { ok: response.ok, status: response.status, body: parsed, outcome: classify(response.status) };
    } catch (error) {
      // A timeout or a dropped connection is indistinguishable from here and
      // both are retryable with the identical request.
      return { ok: false, status: 0, body: null, outcome: OUTCOME.RETRYABLE, networkError: true };
    } finally {
      window.clearTimeout(timer);
    }
  };

  // Storefront paths, relative to the configured app-proxy base.
  const PATHS = Object.freeze({
    visitor: '/visitor',
    definition: '/quizzes/definition',
    start: '/quizzes/sessions/start',
    answer: '/quizzes/sessions/answer',
    complete: '/quizzes/sessions/complete',
    cta: '/quizzes/sessions/cta',
    state: '/quizzes/sessions/state',
    withdraw: '/quizzes/sessions/withdraw'
  });

  const create = (options) => {
    const settings = options || {};
    const timeoutMs = settings.timeoutMs || DEFAULT_TIMEOUT_MS;
    const base = String(settings.proxyBase || '/apps/back9-experiments').replace(/\/+$/, '');

    // The mock speaks the same envelope as `post`, so nothing downstream can
    // tell the difference. It is the only branch in the whole adapter.
    const mockTransport = settings.mode === 'mock' && window.Back9QuizMock
      ? window.Back9QuizMock.createTransport(settings.mock || {})
      : null;

    const send = (path, payload) => (mockTransport
      ? mockTransport.send(path, payload)
      : post(base + path, payload, timeoutMs));

    return Object.freeze({
      mode: mockTransport ? 'mock' : 'proxy',
      visitor: (payload) => send(PATHS.visitor, payload),
      definition: (payload) => send(PATHS.definition, payload),
      start: (payload) => send(PATHS.start, payload),
      answer: (payload) => send(PATHS.answer, payload),
      complete: (payload) => send(PATHS.complete, payload),
      cta: (payload) => send(PATHS.cta, payload),
      state: (payload) => send(PATHS.state, payload),
      withdraw: (payload) => send(PATHS.withdraw, payload)
    });
  };

  window.Back9QuizApi = Object.freeze({ create, randomId, timestamp, classify, OUTCOME });
})();
